import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Layers, Palette, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "../ui/button";
import FloatingIcons from "../FloatingIcons";
import IconTile from "../IconTile";

const HERO_POINTS = [
  { icon: Layers, title: "One login", desc: "Every Agency OS product" },
  { icon: Palette, title: "100% white-label", desc: "Your brand, domain, prices" },
  { icon: Clock, title: "24/7 automation", desc: "AI that runs while you sell" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100dvh] flex items-center overflow-hidden bg-[#F4F6FB]"
    >
      {/* Decorative background layer (behind text) */}
      <div className="absolute inset-0 z-0">
        <div className="mb-blob mb-blob-orange mb-anim-a w-[420px] h-[420px] -top-24 -right-16" />
        <div className="mb-blob mb-blob-blue mb-anim-b w-[380px] h-[380px] top-40 -left-24" />
        <div className="absolute inset-0 mb-grid-lines opacity-70" />
        <div className="absolute inset-0 mb-radial-glow" />
        <FloatingIcons />
      </div>

      {/* Soft white focus glow to keep text readable over icons */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] w-[860px] h-[520px] max-w-[95vw] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(244,246,251,0.92) 0%, rgba(244,246,251,0.7) 42%, rgba(244,246,251,0) 72%)",
        }}
      />

      {/* Content layer */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-8 pt-24 pb-16 w-full">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center"
          >
            <Link
              to="/agency-os"
              data-testid="hero-agency-os-badge"
              className="group inline-flex items-center gap-2 rounded-full border border-[#E6E9F1] bg-white px-4 py-1.5 mb-6 shadow-[0_2px_10px_rgba(11,21,51,0.05)] mb-bob transition-colors hover:border-[#FF6B00]/40"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B00]" />
              <span className="text-xs font-medium text-[#475569]">
                Meet Agency OS - your own white-label AI platform
              </span>
              <ArrowRight className="w-3 h-3 text-[#FF6B00] transition-transform group-hover:translate-x-0.5" />
            </Link>
          </motion.div>

          {/* Headline, logo and intro render without an entrance animation:
              they are the page's largest paint, and fading them in from
              opacity 0 once the JS bundle loads delays LCP. */}
<h1
            data-testid="hero-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]"
          >
            Centralized Dashboard
            <br />
            <span className="mb-gradient-text">For All Channels</span>
          </h1>

          <img
            src="/logos/magicbolt-ai-automation.webp"
            alt="Automate and rank across Google Business, Facebook, Instagram, WhatsApp, WordPress and more"
            width="480"
            height="270"
            decoding="async"
            className="mt-4 mx-auto w-full max-w-2xl h-auto rounded-2xl"
          />

          <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed max-w-2xl mx-auto">
            Run Google, Facebook, Instagram, WhatsApp, AI calling, WordPress and QR review tools
            from one dashboard, then sell it all under{" "}
            <span className="font-semibold text-[#0B1533]">your</span> brand, domain and prices.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button
              data-testid="hero-cta-primary"
              size="lg"
              className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold rounded-xl h-12 px-7 text-base shadow-[0_10px_24px_rgba(255,107,0,0.30)] hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(255,107,0,0.40)] active:translate-y-0 active:scale-[0.98]"
              asChild
            >
              <Link to="/agency-os">
                Start Your White-Label SaaS <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-[#CBD2E0] bg-white/70 text-[#0B1533] hover:bg-white hover:text-[#0B1533] font-semibold rounded-xl h-12 px-7 text-base hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98]"
              asChild
            >
              <Link to="/pricing">See Pricing</Link>
            </Button>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 grid gap-3 sm:grid-cols-3 text-left"
          >
            {HERO_POINTS.map(({ icon, title, desc }) => (
              <li
                key={title}
                className="group flex items-center gap-3.5 rounded-2xl border border-white bg-white/80 p-4 backdrop-blur-sm mb-card-shadow transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/30 hover:shadow-[0_16px_36px_rgba(255,107,0,0.14)]"
              >
                <IconTile icon={icon} size="sm" />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-[#0B1533]">{title}</div>
                  <div className="text-xs text-[#64748B] leading-snug">{desc}</div>
                </div>
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-6 flex flex-col sm:flex-row gap-x-6 gap-y-2 justify-center items-center text-sm font-semibold"
          >
            <Link
              to="/how-agencies-earn"
              data-testid="hero-how-agencies-earn-cta"
              className="group inline-flex items-center gap-1.5 text-[#FF6B00] hover:text-[#E65F00]"
            >
              <TrendingUp className="w-4 h-4" /> How Agencies Earn
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/start"
              data-testid="hero-earn-cta"
              className="text-[#475569] underline decoration-[#CBD2E0] underline-offset-4 hover:text-[#0B1533]"
            >
              How much can you earn? Find out
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
